/* eslint-disable no-unused-vars */

import http from 'common-module/src/util/http';
import axios, { AxiosResponse, CancelToken } from 'axios';
import { md5 } from 'hash-wasm';
import { pick } from 'lodash';
import {
  BindOption,
  BindResponse,
  MD5,
  RemoteFile,
  SliceInfoResponse,
  UploadItemInfo,
  UploadOption,
  UploadProgressCallback
} from './model';
import { isOverseasVersion } from 'common-module/src/util';

export const FileSizeNeedPartUpload = 1024 * 1024 * 20;
export const FileSliceSize = 1024 * 1024 * 20;
export const LargeFileSize = 1024 * 1024 * 500;

export class UploadService {
  private _uploadedMap: Map<MD5, RemoteFile> = new Map();
  private _uploadingMap: Map<MD5, Promise<RemoteFile>> = new Map();
  private _calculatingFile: Set<File> = new Set();

  private _callbackMap: Map<MD5, Array<(progress: number) => void>> = new Map();

  public async uploadFile(option: UploadOption): Promise<RemoteFile> {
    this._check(option);
    return this._uploadFile(option);
  }

  public async uploadFileWithBind(
    uploadOption: UploadOption,
    bindOptions: BindOption
  ): Promise<RemoteFile & BindResponse> {
    const file = await this.uploadFile(uploadOption);
    const bindResult = await this.bind(file, bindOptions, uploadOption);
    return {
      ...{ ...file, ...bindResult.fileUploadSuccessVO },
      ...bindResult
    };
  }

  public async bind(
    remoteFile: RemoteFile,
    bindOptions: BindOption,
    uploadOption: UploadOption
  ): Promise<BindResponse> {
    if (!remoteFile) {
      throw new Error('Bind need file uploaded');
    }

    return (await http.post('sys-file/upload/bind-entity', {
      ...bindOptions,
      fileSize: remoteFile.fileSize,
      fileId: remoteFile.fileId
    }, {
      cancelToken: uploadOption.cancelToken
    })) as Promise<BindResponse>;
  }

  private async _check(option: UploadOption) {
    if (!option.file) {
      throw new Error('File2 is required');
    }
  }

  private async _uploadFile(option: UploadOption): Promise<RemoteFile> {
    const info = await this._calculateUploadInfo(option);

    await this._waitForAllFillCalculateMD5();

    if (info.md5) {
      if (this._uploadedMap.has(info.md5)) {
        return this._uploadedMap.get(info.md5)!;
      }
      if (this._uploadingMap.has(info.md5)) {
        return this._uploadingMap.get(info.md5)!;
      }
    }

    const promise = this._uploadFileInternal(info);

    if (info.md5) {
      this._uploadingMap.set(info.md5, promise);

      if (option.onProgress) {
        this._callbackMap.set(info.md5, [
          ...(this._callbackMap.get(info.md5) || []),
          (process) => option.onProgress?.(process, info)
        ]);
      }
    }

    try {
      const result = await promise;
      if (info.md5) {
        this._uploadingMap.delete(info.md5);
        this._uploadedMap.set(info.md5!, result);
      }
      return result;
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      if (info.md5) {
        this._callbackMap.delete(info.md5);
        this._uploadingMap.delete(info.md5);
      }
    }
  }

  private async _waitForAllFillCalculateMD5(): Promise<void> {
    while (this._calculatingFile.size > 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  private onInnerProgress = (process: number, info: UploadItemInfo) => {
    if (info.md5) {
      const callbacks = this._callbackMap.get(info.md5);
      if (callbacks) {
        callbacks.forEach((callback) => callback(process));
      }
    }
  };

  private async _calculateUploadInfo(
    option: UploadOption
  ): Promise<UploadItemInfo> {
    if (option.file.size > LargeFileSize) {
      return {
        ...option,
        fileSize: option.file.size,
        isLargeFile: true,
        isUseSliceUpload: true
      };
    }

    if (isOverseasVersion && option.file.name.toLocaleLowerCase().endsWith('.drawio')) {
      return {
        ...option,
        fileSize: option.file.size,
        isLargeFile: false,
        isUseSliceUpload: false
      };
    }
    
    const buffer = await option.file.arrayBuffer();
    this._calculatingFile.add(option.file);
    const md5 = await this._calculateMD5(buffer);
    this._calculatingFile.delete(option.file);

    return {
      ...option,
      fileSize: option.file.size,
      isLargeFile: false,
      isUseSliceUpload: option.file.size > FileSizeNeedPartUpload,
      md5,
      arrayBuffer: buffer
    };
  }

  private async _calculateMD5(buffer: ArrayBuffer): Promise<MD5> {
    const u8Buffer = new Uint8Array(buffer);
    return md5(u8Buffer);
  }

  private async _uploadFileInternal(info: UploadItemInfo): Promise<RemoteFile> {
    const request = new UploadRequestItem(
      info,
      this.onInnerProgress.bind(this)
    );
    return request.run();
  }
}

class UploadRequestItem {
  private get _slicePartCount() {
    return this.info.isUseSliceUpload
      ? Math.ceil(this.info.fileSize / FileSliceSize)
      : 1;
  }

  private _eTags: string[] = [];
  private _uploadInfo?: SliceInfoResponse;

  private _remoteFile?: RemoteFile;

  // eslint-disable-next-line no-unused-vars
  public constructor(
    public readonly info: UploadItemInfo,
    private readonly innerOnProgress: UploadProgressCallback
  ) {}

  private async _requestUploadUrls(info: UploadItemInfo) {
    return http.post('sys-file/upload/create-multipart-upload', {
      ...pick(info, ['fileSize', 'md5', 'channel']),
      fileName: info.file.name,
      sliceLength: FileSliceSize,
      sliceNumber: this._slicePartCount
    }) as Promise<SliceInfoResponse>;
  }

  public async run(): Promise<RemoteFile> {
    this._uploadInfo = await this._requestUploadUrls(this.info);

    if (this._uploadInfo.existedFile?.fileKey) {
      return this._uploadInfo.existedFile;
    }

    if (this._uploadInfo.urls.length !== this._slicePartCount) {
      throw new Error('Upload urls count not match');
    }
    this.info.cancelToken?.throwIfRequested();

    for (let index = 0; index < this._slicePartCount; index++) {
      const url = this._uploadInfo.urls[index];
      const promise = this._uploadSlicePart(
        index,
        url,
        (process) => {
          this.innerOnProgress(
            (index + process) / this._slicePartCount,
            this.info
          );
        },
        this.info.cancelToken
      );
      // eslint-disable-next-line no-await-in-loop
      this._eTags.push((await promise).headers.etag.replaceAll('"', ''));
    }
    this._remoteFile = await this._notifyUploadComplete();

    return this._remoteFile;
  }

  private async _notifyUploadComplete() {
    if (!this._uploadInfo) {
      throw new Error('Upload info not found');
    }
    return http.post('sys-file/upload/notify-finish-upload', {
      ...pick(this.info, ['md5']),
      ...pick(this._uploadInfo, ['fileId']),
      fileName: this.info.file.name,
      mergeDTO: {
        uploadId: this._uploadInfo.id,
        etags: this._eTags,
        key: this._uploadInfo.key
      }
    }, {
      cancelToken: this.info.cancelToken
    }) as Promise<RemoteFile>;
  }

  private _uploadSlicePart(
    index: number,
    url: string,
    onProgress: (process: number) => void,
    cancelToken?: CancelToken
  ) {
    cancelToken?.throwIfRequested();

    const blob = this._slicePart(this.info.file, index);
    const type =
      this._uploadInfo?.contentType ||
      (this.info.file.name.endsWith('.svg') ? 'image/svg+xml' : blob.type);

    const encodeFileName = (name: string) => {
      const encode = encodeURIComponent(name);
      return encode.replace(/[!'()*]/g, (c) => {
        return '%' + c.charCodeAt(0).toString(16);
      });
    };

    return axios.put(url, blob, {
      headers: {
        'content-type': type,
        'Content-Disposition': `attachment; filename="${encodeFileName(
          this.info.file.name
        )}"`
      },
      cancelToken,
      onUploadProgress: (e) => {
        onProgress?.(e.loaded / e.total);
      }
    }) as Promise<AxiosResponse>;
  }

  private _slicePart(file: File, index: number) {
    const start = index * FileSliceSize;
    const end = Math.min(start + FileSliceSize, file.size);
    const slice = file.slice(start, end);
    return slice;
  }
}
